import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, Recycle, Heart as HeartIcon, Package } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { supabase } from '../supabaseClient';
import homeImage from '../assets/home.jpeg';
import { collections, testimonials } from '../data/mockData';

const Home = () => {
  
  const [products, setProducts] = useState([]);
  const bestSellers = products.slice(0, 4);
      useEffect(() => {
      const fetchProducts = async () => {
        const { data } = await supabase
          .from('products')
          .select('*');
        setProducts(data);
      };

      fetchProducts();
    }, []);

  return (
    <div className="bg-cream">
      {/* Hero Section */}
      <section className="relative h-[85vh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={homeImage}
            alt="Luxury Fashion"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/30"></div>
        </div>
        
        <div className="relative z-10 text-center text-white px-6 animate-fade-in">
          <p className="text-xs uppercase tracking-[0.3em] mb-6 font-light">
            Ethical Luxury
          </p>
          <h1 className="font-serif text-5xl md:text-7xl font-normal mb-8 leading-tight">
            Luxury That Breathes.<br />
            <span className="italic font-light">Live Your Values.</span>
          </h1>
          <Link
              to="/shop"
              className="inline-flex items-center justify-center bg-black text-white px-8 py-3 rounded-lg shadow-md hover:bg-gray-900 hover:shadow-lg transition-all duration-300 text-sm tracking-wide font-medium"
              data-testid="hero-cta-button"
            >
              Shop Collection
          </Link>
        </div>
      </section>

      {/* Featured Collections */}
      <section className="py-20 md:py-32 max-w-7xl mx-auto px-6 md:px-12" data-testid="featured-collections">
        <div className="text-center mb-16">
          <h2 className="font-serif text-3xl md:text-5xl text-charcoal mb-4">
            Collections
          </h2>
          <p className="text-[#6B665F] font-light text-lg">
            Timeless pieces, ethically crafted
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {collections.map((collection, index) => (
            <Link
              key={collection.id}
              to={`/category/${collection.name.toLowerCase()}`}
              className="group relative overflow-hidden aspect-[3/4] animate-fade-in"
              style={{ animationDelay: `${index * 100}ms` }}
              data-testid={`collection-${collection.name.toLowerCase()}`}
            >
              <img
                src={collection.image}
                alt={collection.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/30 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="font-serif text-2xl mb-2">{collection.name}</h3>
                <p className="text-sm font-light opacity-90">{collection.description}</p>
                <div className="mt-4 flex items-center gap-2 text-xs uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  Explore <ArrowRight size={14} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Brand Story Preview */}
      <section className="py-20 md:py-32 relative overflow-hidden" data-testid="brand-story-section">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1760878405661-7d0273ebf781?w=1600&q=80"
            alt="Dried flowers"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-bronze mb-6">
            Our Story
          </p>
          <h2 className="font-serif text-3xl md:text-5xl text-charcoal mb-8">
            Fashion That Respects<br />
            <span className="italic font-light">People & Planet</span>
          </h2>
          <p className="text-lg text-[#6B665F] leading-relaxed font-light mb-8">
            At Siyaanish, we believe luxury should never come at the cost of our values. 
            Every piece is crafted from sustainable, plant-based fabrics using fair trade 
            practices. We create timeless fashion that honors both artisans and the earth.
          </p>
          <Link
            to="/about"
            className="inline-flex items-center justify-center bg-black text-white px-8 py-3 rounded-lg shadow-md hover:bg-gray-900 hover:shadow-lg transition-all duration-300 text-sm tracking-wide font-medium"
              data-testid="hero-cta-button"
          >
            Read Our Story
          </Link>
        </div>
      </section>

      {/* Best Sellers */}
      <section className="py-20 md:py-32 bg-white" data-testid="best-sellers-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-5xl text-charcoal mb-4">
              Best Sellers
            </h2>
            <p className="text-[#6B665F] font-light text-lg">
              Pieces our community loves most
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-16">
            {bestSellers.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          <div className="text-center mt-16">
            <Link
              to="/shop"
              className="inline-flex items-center justify-center bg-black text-white px-8 py-3 rounded-lg shadow-md hover:bg-gray-900 hover:shadow-lg transition-all duration-300 text-sm tracking-wide font-medium"
              data-testid="hero-cta-button"
            >
              View All Products
            </Link>
          </div>
        </div> 
      </section>

      {/* Sustainability Section */}
      <section className="py-20 md:py-32 bg-beige" data-testid="sustainability-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-5xl text-charcoal mb-4">
              Conscious Fashion
            </h2>
            <p className="text-[#6B665F] font-light text-lg max-w-2xl mx-auto">
              Every decision we make considers the impact on people and planet
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 text-center border border-[#E5E0D8] hover:shadow-lg transition-shadow duration-500">
              <div className="w-12 h-12 mx-auto mb-4 text-bronze">
                <Leaf size={48} />
              </div>
              <h3 className="font-serif text-xl mb-3 text-charcoal">Plant-Based Fabrics</h3>
              <p className="text-sm text-[#6B665F] font-light leading-relaxed">
                Aloe vera, Eucalyptus, Banana, Rose, Orange and more Cottons and Silk Fibre Fabrics.
              </p>
            </div>

            <div className="bg-white p-8 text-center border border-[#E5E0D8] hover:shadow-lg transition-shadow duration-500">
              <div className="w-12 h-12 mx-auto mb-4 text-bronze">
                <HeartIcon size={48} />
              </div>
              <h3 className="font-serif text-xl mb-3 text-charcoal">Fair Trade</h3>
              <p className="text-sm text-[#6B665F] font-light leading-relaxed">
                Every artisan receives fair wages and works in safe, dignified conditions.
              </p>
            </div>

            <div className="bg-white p-8 text-center border border-[#E5E0D8] hover:shadow-lg transition-shadow duration-500">
              <div className="w-12 h-12 mx-auto mb-4 text-bronze">
                <Package size={48} />
              </div>
              <h3 className="font-serif text-xl mb-3 text-charcoal">Small Batch</h3>
              <p className="text-sm text-[#6B665F] font-light leading-relaxed">
                Limited production runs reduce waste and ensure exceptional quality.
              </p>
            </div>

            <div className="bg-white p-8 text-center border border-[#E5E0D8] hover:shadow-lg transition-shadow duration-500">
              <div className="w-12 h-12 mx-auto mb-4 text-bronze">
                <Recycle size={48} />
              </div>
              <h3 className="font-serif text-xl mb-3 text-charcoal">Zero Waste</h3>
              <p className="text-sm text-[#6B665F] font-light leading-relaxed">
                Plastic-free packaging and carbon-neutral shipping to minimize our footprint.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {/* <section className="py-20 md:py-32" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-5xl text-charcoal mb-4">
              Loved By Our Community
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-white p-8 border border-[#E5E0D8]"
                data-testid={`testimonial-${testimonial.id}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-bronze">★</span>
                  ))}
                </div>
                <p className="text-[#6B665F] font-light leading-relaxed mb-4 italic">
                  "{testimonial.text}"
                </p>
                <p className="text-sm font-medium text-charcoal">— {testimonial.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}
    </div>
  );
};

export default Home;
